// ============================================================================
//  DatabaseTools — find members who have not attended in years
// ============================================================================
//
//  A membership lapses when nobody uses it, so the people worth offering a free
//  renewal to are exactly the ones who have not been seen for a year or more.
//  They are already recorded, across the facility databases on this server.
//  This reads them out and loads the numbers into the renewal register, where
//  they are worked like any other.
//
//  ── THE PASSWORD IS NOT WHAT THIS FILE DOES ────────────────────────────────
//
//  The box below COLLECTS a key and sends it with every request. It does not
//  check it, and nothing here decides whether you are allowed in — the server
//  does, against MAINTENANCE_KEY in its environment, compared in constant time.
//
//  That distinction is the whole of the security. A check written in this file
//  would be readable in the bundle and removable with devtools, and a key
//  hard-coded here would be a key shipped to every facility. Putting this
//  screen inside the Admin Panel is organisation, not protection: the endpoints
//  are equally fenced from anywhere.
//
//  The key is held in component state for the session and never written to
//  localStorage — a password left in browser storage on a shared desk machine
//  outlives the person who typed it.
//
//  ── AND IT READS OTHER FACILITIES' DATA ────────────────────────────────────
//
//  Worth saying plainly where somebody will read it: a scan returns patient
//  names, NHIS numbers and visit dates from whichever database is chosen. Every
//  scan and import is written to the activity log with the database and the
//  count.
import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, TextField, Button, Alert, Chip, MenuItem, Select,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Checkbox,
  CircularProgress, InputAdornment, IconButton, Tooltip, Divider, Snackbar,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DnsIcon from '@mui/icons-material/Dns';
import { dormancyDatabases, dormancyScan, dormancyImport, dormancyServers } from '../BaseURL';
//  Other servers: add, test, edit, remove. The dropdown below picks between
//  them and this server; everything else on the screen works the same on each.
import RemoteServersDialog from './RemoteServersDialog';

const PRIMARY = '#1A9B7F';
const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const LINE = '#E7EBF0';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const CARD = {
  borderRadius: 3, border: `1px solid ${LINE}`, background: '#fff',
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

//  A date N years before today, as YYYY-MM-DD, computed locally so the cut-off
//  matches the day the operator is looking at rather than UTC.
const yearsAgo = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const PERIODS = [
  { key: 1, label: 'Over 1 year' },
  { key: 2, label: 'Over 2 years' },
  { key: 3, label: 'Over 3 years' },
  { key: 5, label: 'Over 5 years' },
];

//  "Used last year, not this year": last seen at some point in the previous
//  calendar year and not since. The scan's date-range mode asks exactly that of
//  the LAST visit, so this is a preset of it rather than a new query.
const LAST_YEAR = (() => {
  const y = new Date().getFullYear() - 1;
  return { from: `${y}-01-01`, to: `${y}-12-31`, year: y };
})();

const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || ''; }
  catch { return ''; }
};

export default function DatabaseTools() {
  const me = useMemo(currentUser, []);

  const [key, setKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [databases, setDatabases] = useState([]);
  const [skipped, setSkipped] = useState([]);

  //  0 is this server; anything else is a saved one.
  const [servers, setServers] = useState([]);
  const [server, setServer] = useState(0);
  const [serversOpen, setServersOpen] = useState(false);
  const serverLabel = server
    ? (servers.find((s) => s.id === server)?.label || 'another server') : 'this server';

  const [db, setDb] = useState('');
  const [mode, setMode] = useState('before');
  const [years, setYears] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(500);

  const [result, setResult] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // ── the saved servers ────────────────────────────────────────────────────
  const loadServers = useCallback(async (k = key) => {
    try {
      const { data } = await axios.post(dormancyServers, { key: k });
      setServers(data.servers || []);
    } catch { /* the list is a convenience; this server still works without it */ }
  }, [key]);

  //  The databases on one server. A failure on another server is shown, but
  //  never locks the screen — this server stays usable.
  const loadDatabases = useCallback(async (serverId, k = key) => {
    setBusy('databases'); setError(null);
    setDatabases([]); setSkipped([]); setDb(''); setResult(null); setPicked(new Set());
    try {
      const { data } = await axios.post(dormancyDatabases, {
        key: k, ...(serverId ? { connection: serverId } : {}),
      });
      setDatabases(data.databases || []);
      setSkipped(data.skipped || []);
      if ((data.databases || []).length === 1) setDb(data.databases[0]);
      return true;
    } catch (err) {
      const r = err?.response;
      if (r?.status === 401) throw err;   // the key itself — handled by unlock
      setError(r?.data?.message || err.message);
      return false;
    } finally { setBusy(null); }
  }, [key]);

  // ── unlock ───────────────────────────────────────────────────────────────
  const unlock = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!key.trim()) return;
    setBusy('unlock'); setError(null);
    try {
      const { data } = await axios.post(dormancyDatabases, { key });
      setDatabases(data.databases || []);
      setSkipped(data.skipped || []);
      setServer(0);
      setUnlocked(true);
      if ((data.databases || []).length === 1) setDb(data.databases[0]);
      await loadServers(key);
    } catch (err) {
      const r = err?.response;
      setError(r?.data?.message
        || (r?.status === 401 ? 'Not authorised.' : err.message));
      setUnlocked(false);
    } finally { setBusy(null); }
  }, [key, loadServers]);

  const pickServer = useCallback(async (id) => {
    setServer(id);
    try { await loadDatabases(id); } catch { setUnlocked(false); setError('Not authorised.'); }
  }, [loadDatabases]);

  // ── scan ─────────────────────────────────────────────────────────────────
  const scan = useCallback(async () => {
    if (!db) { setError('Choose a database first.'); return; }
    setBusy('scan'); setError(null); setResult(null); setPicked(new Set());
    try {
      const body = {
        key, database: db, limit: Number(limit) || 500, mode,
        ...(server ? { connection: server } : {}),
      };
      if (mode === 'between') { body.from = from; body.to = to; }
      else body.before = yearsAgo(Number(years) || 1);
      const { data } = await axios.post(dormancyScan, body);
      setResult(data);
      //  Everything not already on the register starts ticked — the common
      //  case is "take all of these", and un-ticking a few is less work than
      //  ticking five hundred.
      setPicked(new Set((data.rows || [])
        .filter((r) => !r.already).map((r) => r.membership_no)));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally { setBusy(null); }
  }, [key, db, mode, years, from, to, limit, server]);

  // ── export ───────────────────────────────────────────────────────────────
  const exportCsv = useCallback(() => {
    const rows = (result?.rows || []).filter((r) => picked.has(r.membership_no));
    const src = rows.length ? rows : (result?.rows || []);
    if (!src.length) return;
    //  Quoted and doubled — a name with a comma in it would otherwise split
    //  the row and silently shift every column after it.
    const cell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['NHIS number', 'Name', 'Folder', 'Gender', 'Date of birth',
      'Scheme', 'Last seen', 'Already on register'];
    const body = src.map((r) => [
      r.membership_no, r.full_name, r.folder_no, r.gender, r.dob,
      r.scheme, r.last_seen || 'never', r.already ? 'yes' : 'no',
    ].map(cell).join(','));
    const csv = [head.map(cell).join(','), ...body].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    //  The server's name goes in the file name, so two facilities' lists saved
    //  on the same day cannot be mistaken for each other.
    const tag = String(result.server && result.server !== 'this server' ? `${result.server}-` : '')
      .replace(/[^A-Za-z0-9_-]+/g, '-');
    a.download = `dormant-${tag}${result.database}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result, picked]);

  // ── import ───────────────────────────────────────────────────────────────
  const addToRegister = useCallback(async () => {
    const rows = (result?.rows || []).filter((r) => picked.has(r.membership_no));
    if (!rows.length) { setError('Nothing selected.'); return; }
    setBusy('import'); setError(null);
    try {
      const { data } = await axios.post(dormancyImport, {
        //  Only used for the note on each register row, so it names the server
        //  as well — "From Gina Maternity · user_gina_db".
        key,
        database: result.server && result.server !== 'this server'
          ? `${result.server} · ${result.database}` : result.database,
        user: me, rows,
      });
      setToast({
        kind: data.added ? 'ok' : 'warn',
        text: `${data.added} added to the register`
          + `${data.skipped ? `, ${data.skipped} already there` : ''}`
          + `${data.rejected ? `, ${data.rejected} rejected` : ''}.`,
      });
      //  Re-scan so the "already on register" marks are right, rather than
      //  leaving a list that says these are still new.
      await scan();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally { setBusy(null); }
  }, [key, result, picked, me, scan]);

  const rows = result?.rows || [];
  const selectable = rows.filter((r) => !r.already);
  const allPicked = selectable.length > 0 && selectable.every((r) => picked.has(r.membership_no));

  // ── the lock ─────────────────────────────────────────────────────────────
  if (!unlocked) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 460, mx: 'auto' }}>
        <Paper component="form" onSubmit={unlock} elevation={0} sx={{ ...CARD, p: 3 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: 2.5, display: 'grid',
            placeItems: 'center', background: '#FEF3C7', color: '#92400E', mb: 2,
          }}><LockIcon /></Box>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: INK }}>
            Database Tools
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: .5, mb: 2.5, lineHeight: 1.6 }}>
            Finds members who have not attended for a year or more, so they can be
            offered a free renewal. Reads patient records from the facility
            databases on this server, and on any other servers you add — every
            scan is logged.
          </Typography>

          <TextField
            fullWidth size="small" autoFocus
            type={showKey ? 'text' : 'password'}
            label="Maintenance key"
            value={key} onChange={(e) => setKey(e.target.value)}
            autoComplete="off" name="maintenance-key"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowKey((v) => !v)} edge="end">
                    {showKey ? <VisibilityOffIcon fontSize="small" />
                      : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>
          )}

          <Button type="submit" fullWidth variant="contained" disableElevation
            disabled={!key.trim() || busy === 'unlock'}
            sx={{ mt: 2, background: PRIMARY, fontWeight: 700, borderRadius: 2,
              textTransform: 'none', py: 1.1 }}>
            {busy === 'unlock' ? 'Checking…' : 'Unlock'}
          </Button>

          <Typography sx={{ fontSize: 11, color: FAINT, mt: 2, lineHeight: 1.6 }}>
            The key is checked on the server, not here, and is not remembered
            between sessions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ── unlocked ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center',
          background: `${PRIMARY}14`, color: PRIMARY,
        }}><StorageIcon /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
            Database Tools
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            Dormant members · {databases.length} database
            {databases.length === 1 ? '' : 's'} on {serverLabel}
          </Typography>
        </Box>
        <Chip size="small" icon={<LockIcon sx={{ fontSize: 14 }} />} label="Unlocked"
          sx={{ fontWeight: 700, background: '#ECFDF3', color: '#15803D' }} />
      </Box>

      {/* ── the query ────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ ...CARD, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/*  ── WHICH SERVER ───────────────────────────────────────────────
               This one, or a saved other one. Picking it reloads the database
               list below from that server.  */}
          <Box sx={{ minWidth: 220 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: MUTED,
              textTransform: 'uppercase', letterSpacing: .5, mb: .5 }}>Server</Typography>
            <Box sx={{ display: 'flex', gap: .75 }}>
              <Select size="small" fullWidth value={server}
                onChange={(e) => pickServer(Number(e.target.value))}
                disabled={busy === 'databases'}
                sx={{ borderRadius: 2, fontSize: 13 }}>
                <MenuItem value={0}>This server</MenuItem>
                {servers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: s.last_error ? '#B91C1C' : FAINT, fontFamily: MONO }}>
                        {s.host}{s.last_error ? ' · last test failed' : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Tooltip title="Add or manage other servers">
                <IconButton onClick={() => setServersOpen(true)}
                  sx={{ border: `1px solid ${LINE}`, borderRadius: 2 }}>
                  <DnsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: MUTED,
              textTransform: 'uppercase', letterSpacing: .5, mb: .5 }}>Database</Typography>
            <Select size="small" fullWidth value={db} displayEmpty
              disabled={busy === 'databases'}
              onChange={(e) => { setDb(e.target.value); setResult(null); }}
              sx={{ borderRadius: 2, fontFamily: MONO, fontSize: 13 }}>
              <MenuItem value="" disabled>
                {busy === 'databases' ? `Reading ${serverLabel}…`
                  : databases.length ? 'Choose…' : 'No facility databases found'}
              </MenuItem>
              {databases.map((n) => <MenuItem key={n} value={n} sx={{ fontFamily: MONO }}>{n}</MenuItem>)}
            </Select>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: MUTED,
              textTransform: 'uppercase', letterSpacing: .5, mb: .5 }}>Not seen for</Typography>
            <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap' }}>
              {PERIODS.map((p) => (
                <Chip key={p.key} size="small" clickable label={p.label}
                  variant={mode === 'before' && years === p.key ? 'filled' : 'outlined'}
                  color={mode === 'before' && years === p.key ? 'primary' : 'default'}
                  onClick={() => { setMode('before'); setYears(p.key); }}
                  sx={{ fontWeight: 600 }} />
              ))}
              {(() => {
                const on = mode === 'between' && from === LAST_YEAR.from && to === LAST_YEAR.to;
                return (
                  <Chip size="small" clickable label={`Used ${LAST_YEAR.year}, not since`}
                    variant={on ? 'filled' : 'outlined'} color={on ? 'primary' : 'default'}
                    onClick={() => { setMode('between'); setFrom(LAST_YEAR.from); setTo(LAST_YEAR.to); }}
                    sx={{ fontWeight: 600 }} />
                );
              })()}
              <Chip size="small" clickable label="Date range"
                variant={mode === 'between' && !(from === LAST_YEAR.from && to === LAST_YEAR.to) ? 'filled' : 'outlined'}
                color={mode === 'between' && !(from === LAST_YEAR.from && to === LAST_YEAR.to) ? 'primary' : 'default'}
                onClick={() => setMode('between')} sx={{ fontWeight: 600 }} />
            </Box>
          </Box>

          {mode === 'between' && (
            <>
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                value={from} onChange={(e) => setFrom(e.target.value)} sx={{ width: 170 }} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                value={to} onChange={(e) => setTo(e.target.value)} sx={{ width: 170 }} />
            </>
          )}

          <TextField size="small" type="number" label="Max rows" value={limit}
            onChange={(e) => setLimit(e.target.value)} sx={{ width: 110 }}
            inputProps={{ min: 1, max: 5000 }} />

          <Button variant="contained" disableElevation startIcon={<SearchIcon />}
            onClick={scan} disabled={!db || busy === 'scan'}
            sx={{ background: PRIMARY, fontWeight: 700, borderRadius: 2,
              textTransform: 'none', py: 1 }}>
            {busy === 'scan' ? 'Searching…' : 'Find'}
          </Button>
        </Box>

        {mode === 'before' && (
          <Typography sx={{ fontSize: 11.5, color: FAINT, mt: 1.25 }}>
            Members whose last visit was before <strong>{yearsAgo(Number(years) || 1)}</strong>,
            and those never seen at all. Only 8-digit NHIS numbers are returned.
          </Typography>
        )}
        {mode === 'between' && from === LAST_YEAR.from && to === LAST_YEAR.to && (
          <Typography sx={{ fontSize: 11.5, color: FAINT, mt: 1.25 }}>
            Members whose <strong>last</strong> visit was in {LAST_YEAR.year} — so they came
            last year and have not been back this year. Only 8-digit NHIS numbers are returned.
          </Typography>
        )}
        {skipped.length > 0 && (
          <Typography sx={{ fontSize: 11, color: FAINT, mt: .75 }}>
            Not offered (no patient table): {skipped.join(', ')}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {busy === 'scan' && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 3 }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Reading {db}…
          </Typography>
        </Box>
      )}

      {/* ── results ──────────────────────────────────────────────────────── */}
      {result && !busy && (
        <Paper elevation={0} sx={{ ...CARD, overflow: 'hidden' }}>
          <Box sx={{ p: 1.75, display: 'flex', gap: 1.5, alignItems: 'center',
            flexWrap: 'wrap', borderBottom: `1px solid ${LINE}` }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
              {Number(result.total).toLocaleString()} dormant
            </Typography>
            {result.shown < result.total && (
              <Typography sx={{ fontSize: 12, color: FAINT }}>
                showing first {Number(result.shown).toLocaleString()}
              </Typography>
            )}
            {/*  Said out loud: on a database with no visit log the answer is
                based on when they REGISTERED, which is a weaker claim and the
                officer should know before acting on it. */}
            <Chip size="small" variant="outlined" label={`by ${result.basis}`}
              sx={{ fontSize: 11 }} />
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED }}>
              {picked.size} selected
            </Typography>
            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}
              disabled={!rows.length}
              sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
              Export CSV
            </Button>
            <Button size="small" variant="contained" disableElevation
              startIcon={<PlaylistAddIcon />} onClick={addToRegister}
              disabled={!picked.size || busy === 'import'}
              sx={{ background: PRIMARY, textTransform: 'none', fontWeight: 700,
                borderRadius: 2 }}>
              {busy === 'import' ? 'Adding…' : `Add ${picked.size} to register`}
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: '58vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ background: '#FBFCFD' }}>
                    <Checkbox size="small" checked={allPicked}
                      indeterminate={picked.size > 0 && !allPicked}
                      disabled={!selectable.length}
                      onChange={() => setPicked(allPicked ? new Set()
                        : new Set(selectable.map((r) => r.membership_no)))} />
                  </TableCell>
                  {['NHIS number', 'Name', 'Folder', 'Gender', 'Last seen', '']
                    .map((h, i) => (
                      <TableCell key={i} sx={{
                        fontWeight: 700, fontSize: 10.5, letterSpacing: .5,
                        textTransform: 'uppercase', color: MUTED, background: '#FBFCFD',
                      }}>{h}</TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7}>
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: MUTED }}>
                        Nobody matches
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: FAINT, mt: .5 }}>
                        Everyone in {result.database} with an 8-digit number has been
                        seen since then.
                      </Typography>
                    </Box>
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={`${r.folder_no}-${r.membership_no}`} hover
                    sx={{ opacity: r.already ? .55 : 1 }}>
                    <TableCell padding="checkbox">
                      <Tooltip title={r.already ? 'Already on the renewal register' : ''}>
                        <span>
                          <Checkbox size="small" disabled={r.already}
                            checked={picked.has(r.membership_no)}
                            onChange={() => setPicked((s) => {
                              const n = new Set(s);
                              if (n.has(r.membership_no)) n.delete(r.membership_no);
                              else n.add(r.membership_no);
                              return n;
                            })} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 800,
                      letterSpacing: 1, color: INK }}>
                      {r.membership_no}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{r.full_name || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: MUTED, fontFamily: MONO }}>
                      {r.folder_no || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: MUTED }}>{r.gender || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: r.last_seen ? MUTED : '#B45309' }}>
                      {r.last_seen || 'never'}
                    </TableCell>
                    <TableCell>
                      {r.already && (
                        <Chip size="small" label="on register" variant="outlined"
                          sx={{ fontSize: 10, height: 20 }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <Typography sx={{ fontSize: 11, color: FAINT, p: 1.5 }}>
            Added numbers appear in Maintenance ▸ Membership Management as pending,
            ready to be renewed.
          </Typography>
        </Paper>
      )}

      <RemoteServersDialog
        open={serversOpen}
        onClose={() => setServersOpen(false)}
        apiKey={key}
        me={me}
        servers={servers}
        onChanged={async () => {
          await loadServers();
          //  The chosen server was removed: fall back to this one rather than
          //  leaving the dropdown pointing at nothing.
          if (server) {
            try {
              const { data } = await axios.post(dormancyServers, { key });
              if (!(data.servers || []).some((s) => s.id === server)) pickServer(0);
            } catch { /* keep what is showing */ }
          }
        }}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.kind === 'ok' ? 'success' : 'warning'} variant="filled"
          onClose={() => setToast(null)} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
